import {
  CloseOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Empty, Input, Modal, Select, Spin } from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CREATE_jwel } from "../../../Config/Config";

const { Option } = Select;

const CUSTTYPE_MAP = {
  CUSTOMER: "Customer",
  DEALER: "DEALER",
  WORKER: "WORKER",
};

const CUSTTYPE_LABEL = {
  CUSTOMER: "Customer",
  DEALER: "Dealer",
  WORKER: "Worker",
};

const SOURCE_CONFIG = {
  DEALER_MASTER: {
    tableName: "DEALER_MASTER",
    apiPrefix: "Master",                       // <-- add this
    buildQuery: (custType) => {
      const dbType = CUSTTYPE_MAP[custType?.toUpperCase()] ?? "Customer";
      const where = encodeURIComponent(`CustType='${dbType}'`);
      return `GetDataFromGivenTableNameWithWhere?tableName=DEALER_MASTER&where=${where}`;
    },
    fields: {
      name: "Dealername",
      mobile: "Mobilenum",
      mobile2: "MobileNum2",
      city: "CityName",
      state: "STATE",
      station: "STATION",
      gst: "TinNo",
      pan: "Cst",
      idProofFlag: "BCOUPONNO",
    },
  },
  CUSTOMER: {
    tableName: "CUSTOMER",
    apiPrefix: "ERP",                           // <-- add this
    buildQuery: () => `GetDataFromGivenTableName?tableName=CUSTOMER`,
    fields: {
      name: "Custname",
      mobile: "Mobileno",
      mobile2: "Phoneno",
      city: "Area",
      state: "STATE",
      station: "STATION",
      gst: "TINNO",
      pan: null,
      idProofFlag: null,
    },
  },
};

const CustomerSelectDialog = ({
  open,
  onCancel,
  onSelect,
  onAddCustomer,
  custType,
  source = "DEALER_MASTER", // "DEALER_MASTER" (TaxInvoice) | "CUSTOMER" (SalesInvoice)
}) => {
  const tenantName = localStorage.getItem("tenantName");
  const searchRef = useRef(null);

  const cfg = SOURCE_CONFIG[source] || SOURCE_CONFIG.DEALER_MASTER;
  const F = cfg.fields;

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterCity, setFilterCity] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const resolvedLabel =
    CUSTTYPE_LABEL[custType?.toUpperCase()] ?? "Customer";

  /* ── derive distinct cities ──────────────────────────────────────────── */
  const cityOptions = React.useMemo(() => {
    const seen = new Set();
    const cities = [];
    customers.forEach((c) => {
      const city = (c[F.city] || "").trim();
      const key = city.toLowerCase();
      if (city && !seen.has(key)) {
        seen.add(key);
        cities.push(city);
      }
    });
    return cities.sort((a, b) => a.localeCompare(b));
  }, [customers, F.city]);

  /* ── fetch based on resolved source ─────────────────────────────────── */
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const query = cfg.buildQuery(custType);
      const response = await axios.get(
        `${CREATE_jwel}/api/${cfg.apiPrefix}/${query}`,   // <-- was hard-coded "Master"
        { headers: { tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data)) setCustomers(data);
    } catch (err) {
      console.error("CustomerSelectDialog fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [tenantName, cfg, custType]);
  
  useEffect(() => {
    if (open) {
      fetchCustomers();
      setFilterName("");
      setFilterMobile("");
      setFilterCity(null);
      setSelectedId(null);
      setTimeout(() => searchRef.current?.focus(), 200);
    }
  }, [open, fetchCustomers]);

  /* ── client-side filter ──────────────────────────────────────────────── */
  const filtered = customers.filter((c) => {
    const name = (c[F.name] || "").toLowerCase();
    const mobile = (c[F.mobile] || "").toLowerCase();
    const city = (c[F.city] || "").toLowerCase();

    const nameMatch = name.includes(filterName.toLowerCase());
    const mobileMatch = mobile.includes(filterMobile.toLowerCase());
    const cityMatch = !filterCity || city === filterCity.toLowerCase();

    return nameMatch && mobileMatch && cityMatch;
  });

  /* ── helpers ─────────────────────────────────────────────────────────── */
  const handleSelect = (customer) => {
    setSelectedId(customer[F.mobile]);
    onSelect(customer);
  };

  const handleReset = () => {
    setFilterName("");
    setFilterMobile("");
    setFilterCity(null);
  };

  const stationStyle = (station) =>
    (station || "").toUpperCase() === "OUTSTATION"
      ? {
        bg: "rgba(220,111,38,0.12)",
        color: "#dc6f26",
        border: "rgba(220,111,38,0.30)",
      }
      : {
        bg: "rgba(68,100,142,0.10)",
        color: "#44648e",
        border: "rgba(68,100,142,0.28)",
      };

  const avatarBg = (name) => {
    const palette = [
      ["#1e3a5f", "#2d5282"],
      ["#14532d", "#15803d"],
      ["#7c2d12", "#c2410c"],
      ["#312e81", "#4338ca"],
      ["#701a75", "#a21caf"],
      ["#0c4a6e", "#0369a1"],
    ];
    const idx = (name || "A").charCodeAt(0) % palette.length;
    return palette[idx];
  };

  const hasActiveFilter = filterName || filterMobile || filterCity;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      centered
      width={900}
      styles={{
        body: { padding: 0 },
        content: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.20)",
          border: "1px solid #e2e8f0",
          padding: 0,
        },
        mask: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(0,0,0,0.20)",
        },
      }}
    >
      <style>{`
        @keyframes csd-fadein {
          from { opacity:0; transform:translateY(-6px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes csd-rowin {
          from { opacity:0; transform:translateX(5px); }
          to   { opacity:1; transform:translateX(0); }
        }
        .csd-body { animation: csd-fadein 0.24s ease both; }

        .csd-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px 14px;
          border-radius: 10px;
          cursor: pointer;
          border: 1.5px solid transparent;
          transition: all 0.15s ease;
          animation: csd-rowin 0.18s ease both;
          background: #ffffff;
          margin-bottom: 6px;
        }
        .csd-row:hover {
          border-color: #93c5fd;
          background: linear-gradient(135deg,#f0f7ff,#e8f2ff);
          transform: translateX(2px);
          box-shadow: 0 3px 12px rgba(59,130,246,0.12);
        }
        .csd-row.selected {
          border-color: #3b82f6;
          background: linear-gradient(135deg,#eff6ff,#dbeafe);
          box-shadow: 0 3px 14px rgba(59,130,246,0.18);
        }

        .csd-name-filter input { border-radius: 8px !important; font-size: 0.78rem !important; }
        .csd-name-filter input:focus { border-color: #6b7280 !important; box-shadow: 0 0 0 2px rgba(107,114,128,0.15) !important; }

        .csd-mobile-filter input { border-radius: 8px !important; font-size: 0.78rem !important; }
        .csd-mobile-filter input:focus { border-color: #6b7280 !important; box-shadow: 0 0 0 2px rgba(107,114,128,0.15) !important; }

        .csd-city-select .ant-select-selector {
          border-radius: 8px !important;
          font-size: 0.78rem !important;
          border-color: #d1d5db !important;
        }
        .csd-city-select.ant-select-focused .ant-select-selector {
          border-color: #6b7280 !important;
          box-shadow: 0 0 0 2px rgba(107,114,128,0.15) !important;
        }

        .csd-add-btn { transition: all 0.18s ease !important; }
        .csd-add-btn:hover {
          filter: brightness(1.08) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 18px rgba(29,78,216,0.36) !important;
        }

        .csd-scroll::-webkit-scrollbar { width: 5px; }
        .csd-scroll::-webkit-scrollbar-track { background: transparent; }
        .csd-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .csd-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="csd-body">
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div
          style={{
            background: "linear-gradient(135deg,#1e3a5f 0%,#2d5282 100%)",
            padding: "16px 22px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: -26,
              right: -26,
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
              pointerEvents: "none",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              bottom: -14,
              left: "35%",
              width: 50,
              height: 50,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              pointerEvents: "none",
            }}
          />

          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: "rgba(255,255,255,0.14)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UserOutlined style={{ color: "#fff", fontSize: 20 }} />
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "0.3px",
              }}
            >
              Select {resolvedLabel}
            </div>
            <div
              style={{
                fontSize: "0.68rem",
                color: "rgba(255,255,255,0.55)",
                marginTop: 2,
              }}
            >
              {loading
                ? `Loading ${resolvedLabel.toLowerCase()}s…`
                : `${filtered.length} of ${customers.length} ${resolvedLabel.toLowerCase()}s`}
            </div>
          </div>

          <Button
            className="csd-add-btn"
            onClick={() => {
              onCancel();
              onAddCustomer();
            }}
            style={{
              height: 36,
              background: "linear-gradient(135deg,#1d4ed8 0%,#2563eb 100%)",
              border: "none",
              color: "#fff",
              borderRadius: "9px",
              fontWeight: 700,
              fontSize: "0.78rem",
              boxShadow: "0 3px 10px rgba(37,99,235,0.40)",
              display: "flex",
              alignItems: "center",
              gap: 6,
              paddingInline: 14,
            }}
          >
            <UserAddOutlined style={{ fontSize: 14 }} /> Add {resolvedLabel}
          </Button>

          <Button
            type="text"
            onClick={fetchCustomers}
            title="Refresh"
            style={{
              color: "rgba(255,255,255,0.70)",
              width: 32,
              height: 32,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              flexShrink: 0,
            }}
          >
            <ReloadOutlined style={{ fontSize: 13 }} />
          </Button>

          <Button
            type="text"
            onClick={onCancel}
            style={{
              color: "rgba(255,255,255,0.70)",
              width: 32,
              height: 32,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              flexShrink: 0,
            }}
          >
            <CloseOutlined style={{ fontSize: 13 }} />
          </Button>
        </div>

        {/* ── Filter bar ────────────────────────────────────────────────── */}
        <div
          style={{
            background: "linear-gradient(135deg,#f8fafc,#f1f5f9)",
            padding: "11px 18px",
            display: "flex",
            gap: 10,
            alignItems: "center",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Input
            className="csd-name-filter"
            ref={searchRef}
            prefix={<UserOutlined style={{ color: "#9ca3af", fontSize: 12 }} />}
            placeholder={`Filter by name…`}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            allowClear
            style={{ flex: 1.4 }}
          />

          <Input
            className="csd-mobile-filter"
            prefix={
              <PhoneOutlined style={{ color: "#9ca3af", fontSize: 12 }} />
            }
            placeholder="Filter by mobile…"
            value={filterMobile}
            onChange={(e) => setFilterMobile(e.target.value.replace(/\D/g, ""))}
            allowClear
            style={{ flex: 1 }}
          />

          <Select
            className="csd-city-select"
            placeholder={
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: "0.78rem",
                  color: "#9ca3af",
                }}
              >
                <EnvironmentOutlined style={{ fontSize: 12 }} /> All Cities
              </span>
            }
            value={filterCity}
            onChange={(val) => setFilterCity(val ?? null)}
            allowClear
            showSearch
            style={{ flex: 1, minWidth: 0 }}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            dropdownStyle={{ borderRadius: 10, fontSize: "0.78rem" }}
            notFoundContent={
              <span style={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                No cities found
              </span>
            }
          >
            {cityOptions.map((city) => (
              <Option key={city} value={city}>
                {city}
              </Option>
            ))}
          </Select>

          {hasActiveFilter && (
            <Button
              type="text"
              onClick={handleReset}
              title="Clear filters"
              style={{
                height: 32,
                padding: "0 10px",
                fontSize: "0.72rem",
                fontWeight: 600,
                color: "#6b7280",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "#fff",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <CloseOutlined style={{ fontSize: 10 }} /> Clear
            </Button>
          )}
        </div>

        {/* ── List ──────────────────────────────────────────────────────── */}
        <div
          className="csd-scroll"
          style={{
            background: "linear-gradient(145deg,#ffffff 0%,#f8fafc 100%)",
            padding: "12px 16px 14px",
            maxHeight: 430,
            overflowY: "auto",
          }}
        >
          <Spin
            spinning={loading}
            tip={`Loading ${resolvedLabel.toLowerCase()}s…`}
          >
            {!loading && filtered.length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ fontSize: "0.78rem", color: "#9ca3af" }}>
                    {customers.length === 0
                      ? `No ${resolvedLabel.toLowerCase()}s found`
                      : `No ${resolvedLabel.toLowerCase()}s match your filters`}
                  </span>
                }
                style={{ padding: "32px 0" }}
              >
                {customers.length === 0 && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      onCancel();
                      onAddCustomer();
                    }}
                    style={{
                      background: "#1d4ed8",
                      border: "none",
                      borderRadius: 7,
                      fontWeight: 600,
                    }}
                  >
                    Add New {resolvedLabel}
                  </Button>
                )}
                {hasActiveFilter && (
                  <Button
                    size="small"
                    onClick={handleReset}
                    style={{ borderRadius: 7, fontWeight: 600 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Empty>
            )}

            {filtered.map((customer, idx) => {
              const name = customer[F.name] || "—";
              const mobile = customer[F.mobile] || "—";
              const mobile2 = F.mobile2 ? customer[F.mobile2] : null;
              const city = customer[F.city] || "—";
              const state = customer[F.state];
              const station = customer[F.station];
              const gstVal = F.gst ? customer[F.gst] : null;
              const panVal = F.pan ? customer[F.pan] : null;
              const idVal = F.idProofFlag ? customer[F.idProofFlag] : null;

              const isSelected = selectedId === customer[F.mobile];
              const [bg0, bg1] = avatarBg(name);
              const stStyle = stationStyle(station);
              const hasGst = gstVal && gstVal !== "-";
              const hasPan = panVal && panVal !== "-";
              const hasId = idVal && idVal !== "-";

              return (
                <div
                  key={`${customer[F.mobile]}-${idx}`}
                  className={`csd-row${isSelected ? " selected" : ""}`}
                  style={{ animationDelay: `${Math.min(idx * 0.025, 0.4)}s` }}
                  onClick={() => handleSelect(customer)}
                >
                  {/* Avatar — fixed */}
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      background: `linear-gradient(135deg,${bg0},${bg1})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 16,
                      flexShrink: 0,
                      boxShadow: `0 2px 8px ${bg1}55`,
                    }}
                  >
                    {(name || "?")[0].toUpperCase()}
                  </div>

                  {/* Name + city — fixed width, truncates */}
                  <div style={{ width: 260, minWidth: 0, flexShrink: 0 }}>
                    <div
                      style={{
                        fontSize: "0.84rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        fontSize: "0.66rem",
                        color: "#6b7280",
                        marginTop: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <EnvironmentOutlined
                        style={{ fontSize: 10, flexShrink: 0 }}
                      />
                      <span
                        style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {city}
                        {state && state !== "-" ? ` · ${state}` : ""}
                      </span>
                    </div>
                  </div>

                  {/* Mobile — fixed width column, always same X position */}
                  <div style={{ width: 160, flexShrink: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "#374151",
                        fontFamily: "monospace",
                      }}
                    >
                      <PhoneOutlined
                        style={{ color: "#6b7280", fontSize: 11 }}
                      />
                      {mobile}
                    </div>
                    {mobile2 && mobile2 !== "-" && (
                      <div
                        style={{
                          fontSize: "0.64rem",
                          color: "#9ca3af",
                          marginTop: 2,
                          fontFamily: "monospace",
                        }}
                      >
                        {mobile2}
                      </div>
                    )}
                  </div>

                  {/* Spacer fills remaining space so badges stay right-aligned */}
                  <div style={{ flex: 1 }} />

                  {/* Badges — fixed width column */}
                  <div
                    style={{
                      width: 220,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      justifyContent: "flex-end",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.60rem",
                        fontWeight: 700,
                        padding: "2px 7px",
                        borderRadius: "5px",
                        background: stStyle.bg,
                        color: stStyle.color,
                        border: `1px solid ${stStyle.border}`,
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {station || "LOCAL"}
                    </span>
                    {hasGst && (
                      <span
                        style={{
                          fontSize: "0.58rem",
                          fontWeight: 700,
                          padding: "2px 6px",
                          borderRadius: "5px",
                          background: "rgba(251,191,36,0.12)",
                          color: "#b45309",
                          border: "1px solid rgba(251,191,36,0.30)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        GST
                      </span>
                    )}
                    {hasPan && (
                      <span
                        style={{
                          fontSize: "0.58rem",
                          fontWeight: 700,
                          padding: "2px 6px",
                          borderRadius: "5px",
                          background: "rgba(59,130,246,0.10)",
                          color: "#1d4ed8",
                          border: "1px solid rgba(59,130,246,0.25)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        PAN
                      </span>
                    )}
                    {hasId && (
                      <span
                        style={{
                          fontSize: "0.58rem",
                          fontWeight: 700,
                          padding: "2px 6px",
                          borderRadius: "5px",
                          background: "rgba(100,116,139,0.10)",
                          color: "#475569",
                          border: "1px solid rgba(100,116,139,0.25)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <IdcardOutlined style={{ fontSize: 9 }} /> ID
                      </span>
                    )}
                  </div>

                  {/* Select chevron — fixed */}
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: isSelected
                        ? "rgba(59,130,246,0.18)"
                        : "rgba(100,116,139,0.08)",
                      border: isSelected
                        ? "1.5px solid rgba(59,130,246,0.40)"
                        : "1px solid rgba(100,116,139,0.20)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: isSelected ? "#1d4ed8" : "#94a3b8",
                      fontSize: 12,
                      fontWeight: 700,
                      transition: "all 0.15s ease",
                    }}
                  >
                    {isSelected ? "✓" : "›"}
                  </div>
                </div>
              );
            })}
          </Spin>
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <div
          style={{
            padding: "10px 20px",
            borderTop: "1px solid #eef0f4",
            background: "#f8fafc",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: "0.68rem", color: "#9ca3af" }}>
            Click any row to select · {cityOptions.length} cities available
          </span>
          <Button
            onClick={onCancel}
            style={{
              height: 34,
              background: "#ffffff",
              border: "1.5px solid #d1d5db",
              color: "#6b7280",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.78rem",
            }}
          >
            <CloseOutlined style={{ fontSize: 11 }} /> Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CustomerSelectDialog;